import React, { useCallback, useEffect, useState } from "react";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
} from "../../../components/table/SummaryTable";
import { ChartAreaLoader } from "../../../components/Loading";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { getMonthlyPriceForecast } from "../../../Rest_api/RestApi";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import FilterMenu from "../../../components/FilterMenu";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import { ExchangeDataTabs } from "../../Rajasthan/Demand/Data";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

export const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const PRICE_TYPE_OPTIONS = [
  { key: "simple_avg", label: "Simple Avg." },
  { key: "wt_avg", label: "Wt. Avg." },
];

const IexMonthly = () => {
  const getChartSuffix = () => {
    const baseSuffix = [];

    if (selectedPriceType.key === "wt_avg") {
      baseSuffix.push({
        label: "",
        value: "actual_wt_avg_mcp",
      });
    } else {
      baseSuffix.push({
        label: "",
        value: "actual_mcp",
      });
    }

    return baseSuffix;
  };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedPriceType, setSelectedPriceType] = useState(
    PRICE_TYPE_OPTIONS[1],
  ); // Default to wt_avg

  // Read view mode from Redux store
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    try {
      const rangeData = await getMonthlyPriceForecast(
        selectedPriceType.key as "simple_avg" | "wt_avg",
      );

      const transformedData = rangeData
        .map((item: any) => ({
          ...item,
          actual_mcp: item.avg_price_actual,
          actual_wt_avg_mcp: item.actual_wt_avg_price,
          month: `${monthNames[item.month - 1]} ${item.year}`,
          monthGroup: monthNames[item.month - 1],
        }))
        .sort(
          (a: { monthGroup: string }, b: { monthGroup: string }) =>
            monthNames.indexOf(a.monthGroup) - monthNames.indexOf(b.monthGroup),
        );

      setData(transformedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedPriceType.key]);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  useEffect(() => {
    let filteredData = data;
    const par: ChartDataParams = {
      keys: ["monthGroup"],
      tableKeys: ["monthGroup", "month", "year"],
      groupBy: ["year"],
      volumeKey: ``,
      aggregationKeys:
        selectedPriceType.key === "wt_avg"
          ? ["actual_wt_avg_mcp"]
          : ["actual_mcp"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [data, selectedPriceType.key]);

  const createChartObject = (
    key: string,
    index: number,
    value: string,
    label: string,
  ) => {
    const [barMonth, barYear] = key.split(" ");
    const now = new Date();
    const currentMonth = monthNames[now.getMonth()];
    const currentYear = now.getFullYear().toString();

    const isCurrentMonthYear =
      barMonth === currentMonth && barYear === currentYear;
    const isAug2025 = barMonth === "Aug" && barYear === "2025";

    return {
      dataKey: `${key}_${value}`,
      name: `${isCurrentMonthYear ? "Forecast " : ""}${normalizeDisplayLabelWithoutUndefined(key.replace("DAM", ""))} ${label}`,
      legendColor: undefined,
      type: ChartType.Bar,
      colorFormatter: (dataIndex: number, dataItem: any) => {
        return isAug2025 ? "#FF5733" : undefined; // Orange color for Aug 2025
      },
    };
  };

  function getLegends() {
    return getChartSuffix().flatMap(({ label, value }) =>
      chartKeys.map(
        (key, index) => createChartObject(key, index, value, label) as any,
      ),
    );
  }

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: tableData,
    itemsPerPage: 96,
    unit: "Rs/KWh",
    extraKeys: ["monthGroup"],
    extraLabels: ["Month"],
    filename: "IEX_Monthly_Price_Forecast",
    showTotal: false,
  };

  const currentMonthIndex = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const currentMonthName = monthNames[currentMonthIndex];

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Price Type"
            items={PRICE_TYPE_OPTIONS.map((option) => option.label)}
            selectedItems={[selectedPriceType.label]}
            onSelect={(item, index) => {
              const option = PRICE_TYPE_OPTIONS.find(
                (opt) => opt.label === item,
              );
              if (option) setSelectedPriceType(option);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading IEX Monthly Price Forecast"
              subtitle="Fetching IEX DAM monthly price forecast data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">DAM Price Forecast (IEX)</h2>
              </div>

              <div className="h-full w-full relative">
                {dataTab?.label === "Chart" && (
                  <ReMixChart
                    chartContainerHeight="calc(100vh - 250px)"
                    data={chartData}
                    showBrush={chartData.length > 96 ? true : undefined}
                    brushStart={BrushStart.Start}
                    isTimeSlot={false}
                    onlyTitle={true}
                    unit={"Rs/KWh"}
                    yAxisLabel={"Rs/KWh"}
                    secondYAxisLabel={"Rs/KWh"}
                    yMaxValue={10}
                    legends={(getLegends() as any).reverse()}
                    defaultSelectedLegends={getLegends().filter((legend: any) =>
                      legend.name.includes("2025"),
                    )}
                    xDataKey={"monthGroup"}
                    xTick={(value: any, index: number): React.ReactNode => {
                      return renderHourTick(value, 10);
                    }}
                    isPriceForecast={true}
                    secondXDataKey={undefined}
                  />
                )}
                {dataTab?.label === "Summary" && (
                  <div
                    style={{ height: "calc(100vh - 200px)" }}
                    className=" overflow-auto"
                  >
                    <LegendSummaryTable {...(tablePar as any)} />
                  </div>
                )}
                {dataTab?.label === "Table" && (
                  <ChartTable {...(tablePar as any)} />
                )}
                <div className="flex justify-between items-center relative">
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#666",
                      marginTop: "8px",
                    }}
                  >
                    <em>Note:</em> (1) MCP reflects a{" "}
                    {selectedPriceType.key === "wt_avg" ? "weighted" : "simple"}{" "}
                    average, (2) The values for{" "}
                    <strong>
                      {currentMonthName} {currentYear}
                    </strong>{" "}
                    are forecasted estimates.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5 ">
            <p className="gna-footer-text col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
};

export default IexMonthly;
